import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminTabButton,
  AdminPanelTitle,
  AdminStatusBadge,
  AdminRowAction,
  AdminMainText,
  AdminSupportingText,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
  ADMIN_FORM_PANEL_CLASS,
} from '../shared/adminUx';

const DATE_LABEL = '10 Aug 2026';

/** `processing` opens System Logs → System Activity (same destination; renamed tab label). */
const logTabByDestination = {
  security: 'security',
  integrations: 'integrations',
  notifications: 'notifications',
  api: 'api',
  processing: 'processing',
  health: 'health',
  activity: 'activity',
};

const providerRows = {
  identity: [
    {
      serviceName: 'Auth0',
      tag: 'Platform Authentication',
      status: 'Warning',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 20:20 AEST`,
      lastErrorOrFailure: 'Existing tenant, applications and Actions require verification',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'auth0',
      consoleUrl: 'https://manage.auth0.com',
      sensitiveConsole: true,
    },
    {
      serviceName: 'Microsoft Entra ID',
      tag: 'Workforce Access',
      status: 'Connected',
      primaryTimeLabel: 'Last Sync',
      primaryTimeValue: `${DATE_LABEL} · 20:14 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'microsoft-entra-id',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Cloudflare Access',
      tag: 'Admin Access Gate',
      status: 'Connected',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 20:11 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'cloudflare-access',
      consoleUrl: 'https://dash.cloudflare.com',
    },
    {
      serviceName: 'Twilio Verify',
      tag: 'Mobile Verification',
      status: 'Connected',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 20:08 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Send Test', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'twilio-verify',
      consoleUrl: 'https://console.twilio.com',
    },
    {
      serviceName: 'ABN Lookup Web Services',
      tag: 'ABN Validation',
      status: 'Connected',
      primaryTimeLabel: 'Last Request',
      primaryTimeValue: `${DATE_LABEL} · 20:03 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'abn-lookup',
      consoleUrl: 'https://abr.business.gov.au',
    },
  ],
  payments: [
    {
      serviceName: 'Stripe',
      tag: 'Subscription Billing',
      status: 'Warning',
      primaryTimeLabel: 'Last Sync',
      primaryTimeValue: `${DATE_LABEL} · 19:58 AEST`,
      lastErrorOrFailure: 'Existing account, products, prices and tax rates require verification',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'integrations',
      serviceRef: 'stripe',
      consoleUrl: 'https://dashboard.stripe.com',
      sensitiveConsole: true,
    },
    {
      serviceName: 'Stripe Webhooks',
      tag: 'Subscription Events',
      status: 'Warning',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:57 AEST`,
      lastErrorOrFailure: 'Existing endpoint and signing configuration require verification',
      actions: ['Test Webhook', 'View Logs'],
      logDestination: 'integrations',
      serviceRef: 'stripe-webhooks',
    },
    {
      serviceName: 'Billing Sync Service',
      tag: 'Internal Subscription Sync',
      status: 'Healthy',
      primaryTimeLabel: 'Last Sync',
      primaryTimeValue: `${DATE_LABEL} · 19:56 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs'],
      logDestination: 'integrations',
      serviceRef: 'billing-sync-service',
    },
  ],
  location: [
    {
      serviceName: 'Google Places API — Autocomplete (New)',
      tag: 'Suburb/Postcode Entry',
      status: 'Connected',
      primaryTimeLabel: 'Last Request',
      primaryTimeValue: `${DATE_LABEL} · 19:51 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'google-places-autocomplete',
      consoleUrl: 'https://console.cloud.google.com',
    },
    {
      serviceName: 'Google Geocoding API',
      tag: 'Geocoding',
      status: 'Connected',
      primaryTimeLabel: 'Last Request',
      primaryTimeValue: `${DATE_LABEL} · 19:49 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'google-geocoding-api',
      consoleUrl: 'https://console.cloud.google.com',
    },
    {
      serviceName: 'Google Maps JavaScript API',
      tag: 'Map Display',
      status: 'Connected',
      primaryTimeLabel: 'Last Request',
      primaryTimeValue: `${DATE_LABEL} · 19:47 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'google-maps-js',
      consoleUrl: 'https://console.cloud.google.com',
    },
    {
      serviceName: 'PostGIS Radius Search',
      tag: 'Internal Radius Search',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 19:45 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs'],
      logDestination: 'api',
      serviceRef: 'postgis-radius-search',
    },
  ],
  safety: [
    {
      serviceName: 'Cloudflare Turnstile',
      tag: 'Form-Abuse Protection',
      status: 'Connected',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:41 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'cloudflare-turnstile',
      consoleUrl: 'https://dash.cloudflare.com',
    },
    {
      serviceName: 'Azure AI Content Safety',
      tag: 'Harmful Content Detection',
      status: 'Connected',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:39 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'azure-content-safety',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure AI Language PII',
      tag: 'PII Detection',
      status: 'Connected',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:37 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'azure-pii',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Document Intelligence Read',
      tag: 'OCR',
      status: 'Connected',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:35 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'azure-document-intelligence',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Microsoft Defender for Storage',
      tag: 'Malware Scanning',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 19:33 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'defender-storage',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Google Cloud Web Risk Lookup',
      tag: 'Unsafe URL Detection',
      status: 'Connected',
      primaryTimeLabel: 'Last Request',
      primaryTimeValue: `${DATE_LABEL} · 19:31 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'google-web-risk',
      consoleUrl: 'https://console.cloud.google.com',
    },
  ],
  communication: [
    {
      serviceName: 'Azure Communication Services Email',
      tag: 'Transactional Email',
      status: 'Connected',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:27 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Send Test', 'View Logs', 'Open Console'],
      logDestination: 'notifications',
      serviceRef: 'acs-email',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Dashboard Notification Service',
      tag: 'In-house Notifications',
      status: 'Healthy',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:25 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Send Test', 'View Logs'],
      logDestination: 'notifications',
      serviceRef: 'dashboard-notification-service',
    },
    {
      serviceName: 'Browser Push Service',
      tag: 'Browser Notifications',
      status: 'Warning',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: 'Not checked',
      lastErrorOrFailure: 'Setup and delivery verification required',
      actions: ['Send Test', 'View Logs'],
      logDestination: 'notifications',
      serviceRef: 'browser-push-service',
      sendTestUnavailable: true,
    },
  ],
  content: [
    {
      serviceName: 'WordPress REST API',
      tag: 'Public Blog',
      status: 'Warning',
      primaryTimeLabel: 'Last Sync',
      primaryTimeValue: `${DATE_LABEL} · 19:20 AEST`,
      lastErrorOrFailure: 'Existing installation and content require verification',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'wordpress-rest-api',
      consoleUrl: 'https://wordpress.com',
    },
    {
      serviceName: 'Google Analytics 4',
      tag: 'Website Analytics',
      status: 'Connected',
      primaryTimeLabel: 'Last Request',
      primaryTimeValue: `${DATE_LABEL} · 19:18 AEST`,
      lastErrorOrFailure: '—',
      actions: ['Test Connection', 'View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'google-analytics-4',
      consoleUrl: 'https://analytics.google.com',
    },
    {
      serviceName: 'Google Search Console',
      tag: 'Search Visibility',
      status: 'Connected',
      primaryTimeLabel: 'Last Sync',
      primaryTimeValue: `${DATE_LABEL} · 19:16 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'google-search-console',
      consoleUrl: 'https://search.google.com/search-console',
    },
    {
      serviceName: 'Bing Webmaster Tools',
      tag: 'Search Visibility',
      status: 'Connected',
      primaryTimeLabel: 'Last Sync',
      primaryTimeValue: `${DATE_LABEL} · 19:14 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'api',
      serviceRef: 'bing-webmaster',
      consoleUrl: 'https://www.bing.com/webmasters',
    },
  ],
  infrastructure: [
    {
      serviceName: 'GraphQL Application API',
      tag: 'Internal Application API',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 19:10 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs'],
      logDestination: 'health',
      serviceRef: 'graphql-api',
    },
    {
      serviceName: 'REST and Webhook Endpoints',
      tag: 'Provider Callbacks',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 19:08 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs'],
      logDestination: 'health',
      serviceRef: 'rest-webhooks',
    },
    {
      serviceName: 'Azure Container Apps',
      tag: 'Application Hosting',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 19:06 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'azure-container-apps',
      consoleUrl: 'https://portal.azure.com',
      sensitiveConsole: true,
    },
    {
      serviceName: 'Azure Container Apps Jobs',
      tag: 'Background Jobs',
      status: 'Healthy',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 19:04 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'processing',
      serviceRef: 'azure-container-jobs',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Container Registry',
      tag: 'Container Images',
      status: 'Healthy',
      primaryTimeLabel: 'Last Deployment',
      primaryTimeValue: `${DATE_LABEL} · 18:58 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'azure-container-registry',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'GitHub Actions',
      tag: 'Deployment Pipeline',
      status: 'Healthy',
      primaryTimeLabel: 'Last Deployment',
      primaryTimeValue: `${DATE_LABEL} · 18:57 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'github-actions',
      consoleUrl: 'https://github.com',
    },
    {
      serviceName: 'Azure Database for PostgreSQL',
      tag: 'Platform Database',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:54 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'azure-postgres',
      consoleUrl: 'https://portal.azure.com',
      sensitiveConsole: true,
    },
    {
      serviceName: 'PostgreSQL Backup & Recovery',
      tag: 'Database Recovery',
      status: 'Healthy',
      primaryTimeLabel: 'Last Backup',
      primaryTimeValue: `${DATE_LABEL} · 18:50 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'processing',
      serviceRef: 'postgres-backup',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Blob Storage — Public Media',
      tag: 'Public Media',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:47 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'blob-public',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Blob Storage — Private Evidence',
      tag: 'Private Evidence',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:45 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'blob-private',
      consoleUrl: 'https://portal.azure.com',
      sensitiveConsole: true,
    },
    {
      serviceName: 'Azure Blob Storage — Upload Quarantine',
      tag: 'Untrusted Uploads',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:43 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'blob-quarantine',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Service Bus Standard',
      tag: 'Queues and Background Work',
      status: 'Healthy',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 18:40 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'processing',
      serviceRef: 'azure-service-bus',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Web PubSub for Socket.IO',
      tag: 'Messages and Quotes Delivery',
      status: 'Healthy',
      primaryTimeLabel: 'Last Success',
      primaryTimeValue: `${DATE_LABEL} · 18:38 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'processing',
      serviceRef: 'azure-web-pubsub',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Key Vault',
      tag: 'Environment Secrets',
      status: 'Healthy',
      primaryTimeLabel: 'Last Rotation',
      primaryTimeValue: `${DATE_LABEL} · 18:35 AEST`,
      lastErrorOrFailure: 'Secret values are never displayed',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'azure-key-vault',
      consoleUrl: 'https://portal.azure.com',
      sensitiveConsole: true,
    },
    {
      serviceName: 'Azure Monitor',
      tag: 'Infrastructure Monitoring',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:31 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'azure-monitor',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Application Insights',
      tag: 'Application Telemetry',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:29 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'azure-app-insights',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Azure Log Analytics',
      tag: 'Technical Log Analysis',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:27 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'health',
      serviceRef: 'azure-log-analytics',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Microsoft Defender for Cloud',
      tag: 'Cloud Security',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:25 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'defender-cloud',
      consoleUrl: 'https://portal.azure.com',
    },
    {
      serviceName: 'Cloudflare Pro',
      tag: 'CDN, DNS, WAF and DDoS',
      status: 'Healthy',
      primaryTimeLabel: 'Last Checked',
      primaryTimeValue: `${DATE_LABEL} · 18:23 AEST`,
      lastErrorOrFailure: '—',
      actions: ['View Logs', 'Open Console'],
      logDestination: 'security',
      serviceRef: 'cloudflare-pro',
      consoleUrl: 'https://dash.cloudflare.com',
    },
  ],
};

const IntegrationsConfig = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('identity');
  const [lastActionResult, setLastActionResult] = useState(null);
  const currentPermissionScope = 'owner';

  const tabs = [
    { id: 'identity', label: 'Identity' },
    { id: 'payments', label: 'Payments' },
    { id: 'location', label: 'Location' },
    { id: 'safety', label: 'Safety' },
    { id: 'communication', label: 'Communication' },
    { id: 'content', label: 'Content & Analytics' },
    { id: 'infrastructure', label: 'Infrastructure' },
  ];

  const nowTimestamp = `${DATE_LABEL} · 20:22 AEST`;

  const isActionPermitted = (actionLabel, row) => {
    if (currentPermissionScope === 'owner' || currentPermissionScope === 'co-owner') return true;
    if (currentPermissionScope === 'team') {
      if (actionLabel === 'Open Console' && row?.sensitiveConsole) return false;
      return actionLabel !== 'Edit';
    }
    return actionLabel === 'View Logs';
  };

  const handleAction = (action, row) => {
    if (!isActionPermitted(action, row)) {
      setLastActionResult({
        result: 'Blocked',
        completedOn: nowTimestamp,
        message: `${action} is permission-controlled.`,
        action,
        serviceName: row?.serviceName,
      });
      return;
    }

    if (action === 'Send Test' && row?.sendTestUnavailable) {
      setLastActionResult({
        result: 'Unavailable',
        completedOn: nowTimestamp,
        message: 'Setup and a valid test subscription are required before Send Test.',
        action,
        serviceName: row.serviceName,
      });
      return;
    }

    if (action === 'View Logs' && row?.logDestination) {
      const tab = logTabByDestination[row.logDestination] || 'integrations';
      const provider = row.serviceName || row.serviceRef || '';
      navigate(`/admin/system-logs?tab=${tab}&provider=${encodeURIComponent(provider)}`);
      setLastActionResult({
        result: 'Success',
        completedOn: nowTimestamp,
        message: `Opened System Logs for ${row.serviceName}.`,
        action,
        serviceName: row.serviceName,
      });
      return;
    }

    if (action === 'Open Console') {
      if (row?.consoleUrl) window.open(row.consoleUrl, '_blank', 'noopener,noreferrer');
      setLastActionResult({
        result: 'Success',
        completedOn: nowTimestamp,
        message: `Opened external console for ${row.serviceName}.`,
        action,
        serviceName: row.serviceName,
      });
      return;
    }

    setLastActionResult({
      result: 'Success',
      completedOn: nowTimestamp,
      message: `${action} completed successfully.`,
      action,
      serviceName: row?.serviceName,
    });
  };

  const visibleRows = useMemo(() => providerRows[activeTab] || [], [activeTab]);

  const IntegrationCard = ({ row }) => (
    <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2 flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <AdminPanelTitle>{row.serviceName}</AdminPanelTitle>
            <AdminStatusBadge status={row.status} label={row.status} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            <AdminSupportingText>Tag: {row.tag}</AdminSupportingText>
            <AdminSupportingText>
              {row.primaryTimeLabel}: {row.primaryTimeValue}
            </AdminSupportingText>
            <AdminSupportingText className="sm:col-span-2">
              Current note: {row.lastErrorOrFailure || '—'}
            </AdminSupportingText>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {row.actions.map((actionLabel) => {
            const disabled =
              !isActionPermitted(actionLabel, row) ||
              (actionLabel === 'Send Test' && row.sendTestUnavailable);
            const label =
              actionLabel === 'Open Console' ? (
                <>
                  Open Console <span aria-hidden>↗</span>
                </>
              ) : (
                actionLabel
              );
            return (
              <AdminRowAction
                key={actionLabel}
                type="button"
                allowWhenRestricted
                disabled={disabled}
                onClick={() => handleAction(actionLabel, row)}
              >
                {label}
              </AdminRowAction>
            );
          })}
        </div>
      </div>
    </DashboardMainContainer>
  );

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          <AdminPageTitle>Integrations</AdminPageTitle>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="border-b border-lightgray">
              <div className="flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>

            <div className={ADMIN_TAB_CONTENT_CONTAINER_CLASS}>
              <div className="p-4 divide-y divide-lightgray">
                {visibleRows.map((row) => (
                  <div key={row.serviceRef || row.serviceName} className="py-4 first:pt-0">
                    <IntegrationCard row={row} />
                  </div>
                ))}
              </div>
            </div>
          </DashboardMainContainer>

          {lastActionResult && (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <div className="p-4 space-y-1">
                <AdminSupportingText>
                  Result:{' '}
                  <AdminMainText className="font-medium">{lastActionResult.result}</AdminMainText>
                </AdminSupportingText>
                <AdminSupportingText>Completed On: {lastActionResult.completedOn}</AdminSupportingText>
                <AdminSupportingText>Service: {lastActionResult.serviceName}</AdminSupportingText>
                <AdminSupportingText>Message: {lastActionResult.message}</AdminSupportingText>
              </div>
            </DashboardMainContainer>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default IntegrationsConfig;
